import { useEffect } from 'react';
import { useInvoicesQuery } from '@/lib/query-options';
import { Invoice } from '@/lib/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Receipt, AlertCircle } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { usePostHog } from 'posthog-js/react';

export const InvoiceHistory = () => {
  const posthog = usePostHog();
  const organizationData = useAtomValue(organizationDataAtom);
  const orgId = organizationData?.id;

  const { data: invoiceResponse, isLoading, error } = useInvoicesQuery(orgId!, { enabled: !!orgId });

  useEffect(() => {
    posthog?.capture('invoices_viewed');
  }, [posthog]);

  if (isLoading) {
    return (
      <Card className="rounded-lg border bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice History</h3>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
            <p className="text-gray-900">Loading invoices...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-lg border bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice History</h3>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mb-4" strokeWidth={1} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load invoices</h3>
            <p className="text-gray-900 text-sm">{(error as Error).message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const invoices = invoiceResponse?.invoices || [];

  if (invoices.length === 0) {
    return (
      <Card className="rounded-lg border bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice History</h3>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Receipt className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
            <h3 className="text-lg font-semibold text-gray-500 mb-2">No Invoices Yet</h3>
            <p className="text-gray-500">Invoice history will appear here once you have paid invoices</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(timestamp * 1000));
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-50';
      case 'open':
        return 'text-blue-600 bg-blue-50';
      case 'draft':
        return 'text-gray-700 bg-gray-50';
      case 'void':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  };

  return (
    <Card className="rounded-lg border bg-white">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice History</h3>
          
          <div className="space-y-3">
            {invoices.map((invoice: Invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Receipt className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="font-medium text-sm">
                        {formatAmount(invoice.amount_paid, invoice.currency)}
                      </div>
                      <div className="text-xs text-gray-700">
                        {formatDate(invoice.created)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      invoice.status
                    )}`}
                  >
                    {invoice.status}
                  </span>
                  
                  {invoice.invoice_pdf && (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      className="h-8 px-2"
                    >
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        PDF
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

        {invoiceResponse?.has_more && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-900">
              Showing {invoices.length} of {invoiceResponse.total_count} invoices
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};